import * as v from 'valibot';

/**
 * Comments, mentions, and links you can send to someone.
 *
 * Sharing a workflow was the whole collaboration model, and it is the wrong
 * granularity for most of what people actually want to do: pointing a colleague
 * at step four of one run does not mean giving them standing edit access to the
 * workflow that produced it, and sending somebody an incident writeup should
 * not require them to have an account on the thing that wrote it.
 */
export const CommentSubjectKind = v.picklist(['run', 'step', 'artifact']);
export type CommentSubjectKind = v.InferOutput<typeof CommentSubjectKind>;

export const Comment = v.object({
  id: v.string(),
  subjectKind: CommentSubjectKind,
  subjectId: v.string(),
  /** Denormalized, so authorizing a comment is one read rather than a join. */
  workflowId: v.string(),
  /** Null for a thread root; the root's id for a reply. One level, not a tree. */
  parentId: v.nullable(v.string()),
  body: v.string(),
  /** Reef user ids, parsed at write time so a mention is delivered once. */
  mentions: v.array(v.string()),
  authorId: v.string(),
  authorEmail: v.optional(v.nullable(v.string())),
  createdAt: v.string(),
  editedAt: v.nullable(v.string()),
  resolvedAt: v.nullable(v.string()),
  resolvedBy: v.nullable(v.string()),
});
export type Comment = v.InferOutput<typeof Comment>;

export const CommentWrite = v.object({
  subjectKind: CommentSubjectKind,
  subjectId: v.string(),
  body: v.pipe(v.string(), v.minLength(1), v.maxLength(10_000)),
  parentId: v.optional(v.nullable(v.string())),
});
export type CommentWrite = v.InferOutput<typeof CommentWrite>;

/**
 * Addresses mentioned in a comment body.
 *
 * Matched on the address rather than on a display name, because two people
 * called Dana is a normal thing for a company to have and delivering a mention
 * to the wrong one is worse than not delivering it. Resolution to a reef user
 * happens at write time — an address that matches nobody is left as text.
 */
const MENTION = /@([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

export function mentionedAddresses(body: string): string[] {
  return [...new Set([...body.matchAll(MENTION)].map((match) => match[1]!.toLowerCase()))];
}

/**
 * A link that lets somebody read one thing without an account on the workflow.
 *
 * Same discipline as a webhook token, for the same reason: reef stores only a
 * SHA-256, so the value exists exactly once — in the response to the call that
 * made it. A leaked list of share links is not a leaked set of credentials.
 */
export const ShareSubjectKind = v.picklist(['run', 'artifact']);
export type ShareSubjectKind = v.InferOutput<typeof ShareSubjectKind>;

export const ShareLink = v.object({
  id: v.string(),
  subjectKind: ShareSubjectKind,
  subjectId: v.string(),
  workflowId: v.string(),
  url: v.string(),
  /** Last four characters. Enough to tell two links apart in a list. */
  tokenHint: v.string(),
  createdBy: v.nullable(v.string()),
  createdAt: v.string(),
  expiresAt: v.nullable(v.string()),
  lastUsedAt: v.nullable(v.string()),
  revokedAt: v.nullable(v.string()),
});
export type ShareLink = v.InferOutput<typeof ShareLink>;

export const ShareLinkWrite = v.object({
  subjectKind: ShareSubjectKind,
  subjectId: v.string(),
  /**
   * Days until it stops working. Defaulted rather than optional: a read-only
   * link to a production incident is not something anybody remembers to revoke,
   * and an expiry nobody chose is better than one nobody set.
   */
  expiresInDays: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(365)), 30),
});
export type ShareLinkWrite = v.InferOutput<typeof ShareLinkWrite>;
